import React from 'react';
import {
  Container,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Mail,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import LoadingScreen from 'src/components/LoadingScreen';
import Page from 'src/components/Page';
import useAuth from 'src/hooks/useAuth';
import useInvites from 'src/hooks/useInvites';
import * as backend from 'src/backend';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { MBadge } from 'src/components/@material-extend';

export function InvitationsBadge({ location, children }) {
  const { user } = useAuth();
  const { invites = [] } = useInvites(user?.email);

  // Early return if no user
  if (!user) {
    return children || null;
  }

  // Don't show badge if no invites
  if (!invites?.length) {
    return children || null;
  }

  if (location === 'center') {
    return (
      <MBadge
        badgeContent={invites.length}
        color="error"
        sx={{
          '& .MuiBadge-badge': {
            right: -3,
            left: 'auto',
            top: 3,
          },
        }}
      />
    );
  }

  return (
    <MBadge
      badgeContent={invites.length}
      color="error"
      sx={{ '& .MuiBadge-badge': { right: -10, top: 10 } }}
    >
      {children}
    </MBadge>
  );
}

export default function Invitations() {
  const { t } = useTranslation('translation');
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { invites = [], isLoading, invalidate } = useInvites(user?.email);

  // Early return if no user is loaded yet
  if (!user) {
    return <LoadingScreen />;
  }

  const handleAccept = (teamId, teamName) => () => {
    backend.user
      .acceptInvite(teamId, user.email)
      .then(() => invalidate())
      .then(() =>
        enqueueSnackbar(
          t('account.accepted-invite-to-join', { name: teamName }),
          {
            variant: 'success',
          }
        )
      )
      .catch(() => {
        enqueueSnackbar(t('form.errors.generic'), { variant: 'error' });
      });
  };

  const handleIgnore = (teamId, teamName) => () => {
    backend.user
      .ignoreInvite(teamId, user.email)
      .then(() => invalidate())
      .then(() =>
        enqueueSnackbar(
          t('account.ignored-invite-to-join', { name: teamName }),
          {
            variant: 'success',
          }
        )
      )
      .catch(() => {
        enqueueSnackbar(t('form.errors.generic'), { variant: 'error' });
      });
  };

  return isLoading ? (
    <LoadingScreen />
  ) : (
    <Page title={t('account.invitations-title')}>
      <Container maxWidth="lg">
        {!invites?.length ? (
          <Typography>{t('account.no-invitations')}</Typography>
        ) : (
          <List>
            {invites.map((invite) => (
              <React.Fragment key={invite.team_id}>
                <ListItem>
                  <ListItemIcon>
                    <Mail color="disabled" />
                  </ListItemIcon>
                  <ListItemText
                    primary={invite.name}
                    secondary={invite.formatted_address}
                  />
                  <ListItemIcon>
                    <Tooltip title={t('account.accept-invite')}>
                      <IconButton
                        onClick={handleAccept(invite.team_id, invite.name)}
                        size="large"
                      >
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemIcon>
                  <ListItemIcon>
                    <Tooltip title={t('account.ignore-invite')}>
                      <IconButton
                        onClick={handleIgnore(invite.team_id, invite.name)}
                        size="large"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemIcon>
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Container>
    </Page>
  );
}
